import { CommonModule } from '@angular/common';
import {
    Component,
    InputSignal,
    OutputEmitterRef,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    viewChild,
    viewChildren,
    ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormErrorHighlighterDirective } from '@app/directives/form-error-highlighter.directive';
import { FileType } from '@app/enums/FileTypes';
import { TravelType } from '@app/enums/travel-type.enum';
import { ReceiptEditComponent } from '@app/v2/components/receipt/receipt-edit/receipt-edit.component';
import { ReceiptFormGroup } from '@app/v2/components/receipt/receipt-edit/receipt-edit.formgroup';
import { ReceiptViewComponent } from '@app/v2/components/receipt/receipt-view/receipt-view.component';
import { AlertComponent, AlertData } from '@components/atoms/alert/alert.component';
import {
    CardComponent,
    CardHeaderAdditionalContentDirective,
    CardMode,
    CardSharedContentDirective,
} from '@components/card/card.component';
import {
    MdsButtonComponent,
    MdsDividerComponent,
    MdsExpansionPanelComponent,
    MdsTabComponent,
    MdsTabGroupComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { Caregiver } from '@models/patient';
import { GenericRequest, GenericRequestStatus, TrainTravelType } from '@models/request';
import { StudyTravelNames, SupportTier } from '@models/study';
import { GroupService } from '@services/group/group.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { FormGroupValueOf, buildSnackBar, collectErrors, rxjsComputed, rxjsEffect } from '@utility/utility';
import { getExtension } from 'mime';
import { Moment } from 'moment';
import {
    BehaviorSubject,
    Observable,
    combineLatest,
    defer,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import { CaregiverCheckboxGroupComponent } from './caregiver-checkbox-group/caregiver-checkbox-group.component';
import { TravelRequestCardFormGroup } from './form-groups/travel-request-card.formgroup';
import { CarServiceTravelRequestTabComponent } from './tabs/car-service-travel-request/car-service-travel-request-tab.component';
import { FlightTravelRequestTabComponent } from './tabs/flight-travel-request-tab/flight-travel-request-tab.component';
import { LodgingTravelRequestTabComponent } from './tabs/lodging-travel-request/lodging-travel-request-tab.component';
import { OtherTravelRequestTabComponent } from './tabs/other-travel-request/other-travel-request-tab.component';
import { RentalCarTravelRequestTabComponent } from './tabs/rental-car-travel-request/rental-car-travel-request-tab.component';
import { TrainTravelRequestTabComponent } from './tabs/train-travel-request/train-travel-request-tab.component';

export type Dates = {
    startDate?: Moment;
    endDate?: Moment;
    tabName: string;
    isOneWay?: boolean;
};
@Component({
    selector: 'medpace-travel-request-card',
    templateUrl: './travel-request-card.component.html',
    styleUrls: ['./travel-request-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        CardComponent,
        CardSharedContentDirective,
        CardHeaderAdditionalContentDirective,
        MdsButtonComponent,
        MdsDividerComponent,
        MdsTabGroupComponent,
        MdsTabComponent,
        MdsExpansionPanelComponent,
        AlertComponent,
        FormErrorHighlighterDirective,
        CaregiverCheckboxGroupComponent,
        FlightTravelRequestTabComponent,
        TrainTravelRequestTabComponent,
        LodgingTravelRequestTabComponent,
        RentalCarTravelRequestTabComponent,
        CarServiceTravelRequestTabComponent,
        OtherTravelRequestTabComponent,
        ReceiptEditComponent,
        ReceiptViewComponent,
    ],
})
export class TravelCardComponent {
    private readonly studyStateService = inject(StudyStateService);
    private readonly groupServices = inject(GroupService);
    private readonly snackbarService = inject(SnackbarService);

    public readonly mode = input.required<CardMode>();
    public readonly showSwitchModeButton = input.required<boolean>();
    public readonly showBookButton = input.required<boolean>();
    public readonly formGroup = input.required<TravelRequestCardFormGroup>();
    public readonly isLoading = input<boolean>(false);
    public readonly request = input.required<GenericRequest>();
    public readonly request$ = toObservable(this.request);
    public readonly isUpdate: InputSignal<boolean> = input.required<boolean>();

    public readonly bookViaGetThereForPatient: OutputEmitterRef<void> = output<void>();
    public readonly bookViaGetThereForCaregiver: OutputEmitterRef<Caregiver> = output<Caregiver>();
    public readonly saveEvent: OutputEmitterRef<FormGroupValueOf<TravelRequestCardFormGroup>> =
        output<FormGroupValueOf<TravelRequestCardFormGroup>>();

    // it's public to allow to invoke push() method on receipts only, because only ReceiptEditComponent implements it
    public readonly receipts = viewChildren<ReceiptEditComponent>(ReceiptEditComponent);

    readonly acceptableFileTypes = [FileType.PDF, FileType.JPG, FileType.JPEG, FileType.PNG];
    public readonly accept = this.acceptableFileTypes.map((extensionWithoutDot) => '.' + extensionWithoutDot).join(',');

    public readonly linkedMode = linkedSignal(() => {
        if (!this.isUpdate()) {
            return 'edit';
        }
        return this.mode();
    });

    private readonly onModeChangeEffect = effect(() => {
        const mode = this.linkedMode();
        if (!mode) return;

        if (mode === 'view') this.formGroup().disable({ emitEvent: false });
        else if (mode === 'edit') this.formGroup().enable({ emitEvent: false });
    });

    public readonly study$ = this.studyStateService.getStudy().pipe(shareReplay(1));
    public readonly study = toSignal(this.study$);
    public readonly isLimitedService = computed(() => this.study()?.supportTier === SupportTier.limitedService);

    formGroup$ = toObservable(this.formGroup).pipe(
        // this line in request.component.ts makes it a FormControl at first, not TravelRequestCardFormGroup: 'travelCard: <TravelRequestCardFormGroup>null,' - should be refactored, but for now it may break things
        filter((formGroup) => formGroup instanceof TravelRequestCardFormGroup)
    );
    studyTravelOptions$ = rxjsComputed(() => this.study$.pipe(map((study) => study?.studyInfo?.studyTravelOptions)));
    isTravelOptionAllowed$(optionName: string) {
        return this.studyTravelOptions$.source$.pipe(
            map((studyTravelOptions) => {
                return studyTravelOptions?.find((option) => option.name === optionName) !== undefined; // option allowed
            })
        );
    }
    createIsTravelOptionEnabled$(optionName: StudyTravelNames, needsTravel$: Observable<boolean>) {
        return combineLatest({
            isTravelOptionAllowed: this.isTravelOptionAllowed$(optionName),
            needsTravel: needsTravel$,
        }).pipe(
            map(({ isTravelOptionAllowed, needsTravel }) => {
                return !!isTravelOptionAllowed || !!needsTravel;
            }),
            shareReplay(1)
        );
    }
    // emits every time when yes/no is selected, used to color mds-tabs
    needsFlight$ = rxjsComputed(() =>
        this.formGroup$.pipe(
            switchMap((formGroup) =>
                formGroup.controls.travelDetails.controls.flight.controls.needsTravel.valueChangesStartWithCurrentValue()
            )
        )
    );
    needsTrain$ = rxjsComputed(() =>
        this.formGroup$.pipe(
            switchMap((formGroup) =>
                formGroup.controls.travelDetails.controls.train.controls.needsTravel.valueChangesStartWithCurrentValue()
            )
        )
    );
    needsLodging$ = rxjsComputed(() =>
        this.formGroup$.pipe(
            switchMap((formGroup) =>
                formGroup.controls.travelDetails.controls.lodging.controls.needsTravel.valueChangesStartWithCurrentValue()
            )
        )
    );
    needsRentalCar$ = rxjsComputed(() =>
        this.formGroup$.pipe(
            switchMap((formGroup) =>
                formGroup.controls.travelDetails.controls.rentalCar.controls.needsTravel.valueChangesStartWithCurrentValue()
            )
        )
    );
    needsCarService$ = rxjsComputed(() =>
        this.formGroup$.pipe(
            switchMap((formGroup) =>
                formGroup.controls.travelDetails.controls.carService.controls.needsTravel.valueChangesStartWithCurrentValue()
            )
        )
    );
    needsOther$ = rxjsComputed(() =>
        this.formGroup$.pipe(
            switchMap((formGroup) =>
                formGroup.controls.travelDetails.controls.other.controls.needsTravel.valueChangesStartWithCurrentValue()
            )
        )
    );
    flightEnabled$ = this.createIsTravelOptionEnabled$(
        'Flights',
        this.request$.pipe(map((request) => request.travelRequests.needsAirTravel)) // use request$ instead of formGroup$'s valuechanges - should not change when user clicks yes/no, because you won't be able to enter a tab, which is not allowed but had 'yes' selected before, once you selected 'no' in it
    );

    trainEnabled$ = this.createIsTravelOptionEnabled$(
        'Trains',
        this.request$.pipe(map((request) => request.travelRequests.needsTrainTravel))
    );

    lodgingEnabled$ = this.createIsTravelOptionEnabled$(
        'Hotels',
        this.request$.pipe(map((request) => request.travelRequests.needsLodging))
    );

    rentalCarEnabled$ = this.createIsTravelOptionEnabled$(
        'Rental Cars',
        this.request$.pipe(map((request) => request.travelRequests.needsRentalCar))
    );

    carServiceEnabled$ = this.createIsTravelOptionEnabled$(
        'Car Services',
        this.request$.pipe(map((request) => request.travelRequests.needsCar))
    );

    initialSelectedTabIndex$ = combineLatest({
        flightEnabled: this.flightEnabled$,
        trainEnabled: this.trainEnabled$,
        lodgingEnabled: this.lodgingEnabled$,
        rentalCarEnabled: this.rentalCarEnabled$,
        carServiceEnabled: this.carServiceEnabled$,
    }).pipe(
        map(({ flightEnabled, trainEnabled, lodgingEnabled, rentalCarEnabled, carServiceEnabled }) => {
            if (flightEnabled) return 0; // flight tab index
            if (trainEnabled) return 1; // train tab index
            if (lodgingEnabled) return 2; // lodging tab index
            if (rentalCarEnabled) return 3; // rentalCar tab index
            if (carServiceEnabled)
                return 4; // carService tab index
            else return 5;
        })
    );

    // observable returning departure and return dates from FlightTab whenever something changes in it
    private readonly flightDates$: Observable<Dates | null> = defer(() => {
        const value = this.formGroup().controls.travelDetails.value;
        const needsAirTravel = value.flight?.needsTravel;
        const travelType = value.flight?.travel?.requestDetails?.patientFlightDetails?.travelType;
        const departureDate = value.flight?.travel?.requestDetails?.patientFlightDetails?.departureDate;
        const returnDate = value.flight?.travel?.requestDetails?.patientFlightDetails?.returnDate;
        return combineLatest({
            needsAirTravel:
                this.formGroup().controls.travelDetails.controls.flight.controls.needsTravel.valueChanges.pipe(
                    startWith(needsAirTravel)
                ),
            travelType:
                this.formGroup().controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.patientFlightDetails.controls.travelType.valueChanges.pipe(
                    startWith(travelType)
                ),
            departureDate:
                this.formGroup().controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.patientFlightDetails.controls.departureDate.valueChanges.pipe(
                    startWith(departureDate)
                ),
            returnDate:
                this.formGroup().controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.patientFlightDetails.controls.returnDate.valueChanges.pipe(
                    startWith(returnDate)
                ),
        }).pipe(
            map((value) => {
                const dates: Dates = {
                    startDate: undefined,
                    endDate: undefined,
                    tabName: 'Flight',
                    isOneWay: undefined,
                };
                if (!value.needsAirTravel) return null;
                else if (value.travelType === TravelType.ONE_WAY) {
                    dates.startDate = value.departureDate;
                    dates.endDate = undefined;
                    dates.isOneWay = true;
                } else if (value.travelType === TravelType.ROUND_TRIP) {
                    dates.startDate = value.departureDate;
                    dates.endDate = value.returnDate;
                    dates.isOneWay = false;
                } else return null;

                return dates;
            })
        );
    });

    // observable returning departure and return dates from TrainTab whenever something changes in it
    private readonly trainDates$: Observable<Dates | null> = defer(() => {
        const value = this.formGroup().controls.travelDetails.value;
        const needsTrainTravel = value.train?.needsTravel;
        const travelType = value.train?.travel?.requestDetails?.patientTrainTripDetails?.travelType;
        const departureDate = value.train?.travel?.requestDetails?.patientTrainTripDetails?.departureDate;
        const returnDate = value.train?.travel?.requestDetails?.patientTrainTripDetails?.returnDate;
        return combineLatest({
            needsTrainTravel:
                this.formGroup().controls.travelDetails.controls.train.controls.needsTravel.valueChanges.pipe(
                    startWith(needsTrainTravel)
                ),
            travelType:
                this.formGroup().controls.travelDetails.controls.train.controls.travel.controls.requestDetails.controls.patientTrainTripDetails.controls.travelType.valueChanges.pipe(
                    startWith(travelType)
                ),
            departureDate:
                this.formGroup().controls.travelDetails.controls.train.controls.travel.controls.requestDetails.controls.patientTrainTripDetails.controls.departureDate.valueChanges.pipe(
                    startWith(departureDate)
                ),
            returnDate:
                this.formGroup().controls.travelDetails.controls.train.controls.travel.controls.requestDetails.controls.patientTrainTripDetails.controls.returnDate.valueChanges.pipe(
                    startWith(returnDate)
                ),
        }).pipe(
            map((value) => {
                const dates: Dates = {
                    startDate: undefined,
                    endDate: undefined,
                    tabName: 'Train',
                    isOneWay: undefined,
                };
                if (!value.needsTrainTravel) return null;
                else if (value.travelType === TrainTravelType.OneWay) {
                    dates.startDate = value.departureDate;
                    dates.endDate = undefined;
                    dates.isOneWay = true;
                } else if (value.travelType === TrainTravelType.RoundTrip) {
                    dates.startDate = value.departureDate;
                    dates.endDate = value.returnDate;
                    dates.isOneWay = false;
                } else return null;

                return dates;
            })
        );
    });

    // observable returning checkIn and checkOut dates from LodgingTab whenever something changes in it
    private readonly lodgingDates$: Observable<Dates | null> = defer(() => {
        const value = this.formGroup().controls.travelDetails.value;
        const needsLodging = value.lodging?.needsTravel;
        const checkInDate = value.lodging?.travel?.requestDetails?.checkInDate;
        const checkOutDate = value.lodging?.travel?.requestDetails?.checkOutDate;
        return combineLatest({
            needsLodging:
                this.formGroup().controls.travelDetails.controls.lodging.controls.needsTravel.valueChanges.pipe(
                    startWith(needsLodging)
                ),
            checkInDate:
                this.formGroup().controls.travelDetails.controls.lodging.controls.travel.controls.requestDetails.controls.checkInDate.valueChanges.pipe(
                    startWith(checkInDate)
                ),
            checkOutDate:
                this.formGroup().controls.travelDetails.controls.lodging.controls.travel.controls.requestDetails.controls.checkOutDate.valueChanges.pipe(
                    startWith(checkOutDate)
                ),
        }).pipe(
            map((value) => {
                if (!value.needsLodging) return null;
                else
                    return <Dates>{
                        tabName: 'Lodging',
                        startDate: value.checkInDate,
                        endDate: value.checkOutDate,
                        isOneWay: false,
                    };
            })
        );
    });

    // observable returning pickup and dropOff dates from TrainTab whenever something changes in it
    private readonly carRentalDates$: Observable<Dates | null> = defer(() => {
        const value = this.formGroup().controls.travelDetails.value;
        const needsRental = value.rentalCar?.needsTravel;
        const startDate = value.rentalCar?.travel?.requestDetails?.pickupDate;
        const endDate = value.rentalCar?.travel?.requestDetails?.dropOffDate;
        return combineLatest({
            needsTravel:
                this.formGroup().controls.travelDetails.controls.rentalCar.controls.needsTravel.valueChanges.pipe(
                    startWith(needsRental)
                ),
            startDate:
                this.formGroup().controls.travelDetails.controls.rentalCar.controls.travel.controls.requestDetails.controls.pickupDate.valueChanges.pipe(
                    startWith(startDate)
                ),
            endDate:
                this.formGroup().controls.travelDetails.controls.rentalCar.controls.travel.controls.requestDetails.controls.dropOffDate.valueChanges.pipe(
                    startWith(endDate)
                ),
        }).pipe(
            map((value) => {
                if (!value.needsTravel) return null;
                else
                    return <Dates>{
                        tabName: 'Rental Car',
                        startDate: value.startDate,
                        endDate: value.endDate,
                        isOneWay: false,
                    };
            })
        );
    });

    // observable returning either flight or train dates, combined as 'travelDates'
    private readonly travelDates$: Observable<Dates | null> = combineLatest({
        flight: this.flightDates$,
        train: this.trainDates$,
    }).pipe(
        map((result) => {
            // in cases when only Flight or both Flight and Train are needed return Flight as the more important one, else return Train
            return !!result.flight ? result.flight : result.train;
        })
    );

    private readonly differentTravelDates$: Observable<AlertData> = defer(() =>
        combineLatest({
            travelDates: this.travelDates$,
            lodgingDates: this.lodgingDates$,
            carRentalDates: this.carRentalDates$,
        }).pipe(
            map((dates) => {
                let differentDates = Object.values(dates) // grab the dates as an array
                    .filter((date) => !!date) // it's null if it's set to 'No' - travel option not needed
                    .filter((date) => !!date.startDate || !!date.endDate); // at least one date is not empty

                differentDates = this.filterDates(differentDates); // remove duplicates and empty values, leaving only those tabs that trigger a warning
                let content: string = '';
                if (differentDates.length > 0) {
                    const differentDatesTabNames = differentDates.map((date) => date.tabName);
                    // this will turn tabNames into a readable using commas and 'and' word, to a string like 'Flight, Lodging and Rental Car' or 'Flight and Rental Car'
                    const differentDatesAsString =
                        differentDatesTabNames.length > 2
                            ? `${differentDatesTabNames.slice(0, -2).join(', ')}, ${differentDatesTabNames
                                  .slice(-2)
                                  .join(' and ')}`
                            : differentDatesTabNames.length === 2
                              ? `${differentDatesTabNames.slice(-2).join(' and ')}`
                              : differentDatesTabNames.join('');

                    content =
                        !this.isUpdate() || this.request().status.statusId === GenericRequestStatus.Draft
                            ? `The selected dates are different for ${differentDatesAsString}. Please confirm that the dates are correct.`
                            : `The selected dates are different for ${differentDatesAsString}.`;
                }
                return <AlertData>{ content: content, type: 'caution' };
            })
        )
    );

    protected readonly alerts$ = combineLatest({ differentTravelDates: this.differentTravelDates$ }).pipe(
        map((alerts) => Object.values(alerts).filter((alert) => !!alert.content))
    );

    public changeModeFn = (mode: CardMode, setModeFn: (mode: CardMode) => void) => {
        if (mode === 'edit') {
            setModeFn(mode);
        } else {
            this.formGroup().recursiveUpdateValueAndValidity();
            if (!this.formGroup().valid) {
                this.formGroup().markAllAsTouched();
                buildSnackBar(collectErrors(this.formGroup()), this.snackbarService);
                return;
            }

            setModeFn(mode);
        }
    };

    public save() {
        this.saveEvent.emit(this.formGroup().getRawValue());
    }

    // will accept an array of Dates objects and filter those that have their startDate and endDate duplicated in the array. Will skip empty dates
    private filterDates(dates: Dates[]): Dates[] {
        const result: Dates[] = [];
        for (let i = 0; i < dates.length - 1; i++) {
            for (let j = i + 1; j < dates.length; j++) {
                // compare one Date against all other Dates in the array
                const dateA = dates[i];
                const dateB = dates[j];
                const skipStartDate = !dateA.startDate || !dateB.startDate; // skip if empty values, they do not trigger the warning
                const skipEndDate = !dateA.endDate || !dateB.endDate;
                const identicalStartDate =
                    !!dateA.startDate && !!dateB.startDate && dateA.startDate.isSame(dateB.startDate, 'day');
                const identicalEndDate =
                    !!dateA.endDate && !!dateB.endDate && dateA.endDate.isSame(dateB.endDate, 'day');
                const identicalDatesOrSkip = (skipStartDate || identicalStartDate) && (skipEndDate || identicalEndDate);
                if (!identicalDatesOrSkip) {
                    // those Dates are not empty and do not have duplicates in the array - they will trigger the warning
                    if (!result.includes(dateA)) result.push(dateA);
                    if (!result.includes(dateB)) result.push(dateB);
                }
            }
        }
        return result;
    }

    patchReceipts$ = combineLatest({
        genericRequest: toObservable(this.request),
        formGroup: toObservable(this.formGroup),
    })
        .pipe(
            filter(({ genericRequest, formGroup }) => {
                return !!genericRequest && !!formGroup && formGroup instanceof TravelRequestCardFormGroup; // legacy code sets this form group to a nullish value/formcontrol or something, hence need to verify that it's the correct type
            }),
            tap(({ genericRequest, formGroup }) => {
                formGroup.controls.receipts.clear();
                if (genericRequest.receipts) {
                    genericRequest.receipts.forEach((receipt) => {
                        const formGroup = new ReceiptFormGroup(receipt);
                        this.formGroup().controls.receipts.push(formGroup);
                    });
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    addReceiptClick() {
        this.addReceipt(new ReceiptFormGroup(null));
    }
    private addReceipt(formGroup: ReceiptFormGroup) {
        // copy over er reporting period and tracking id from the last receipt if it exists, to the new one
        const receipts = this.formGroup().controls.receipts;
        if (receipts.length > 0) {
            const lastReceipt = receipts.at(receipts.length - 1) as ReceiptFormGroup;
            formGroup.controls.erReportingPeriod.setValue(lastReceipt.controls.erReportingPeriod.value);
            formGroup.controls.erTrackingId.setValue(lastReceipt.controls.erTrackingId.value);
        }
        this.formGroup().controls.receipts.push(formGroup, { emitEvent: true });
    }
    removeReceipt(receiptFormGroup: ReceiptFormGroup) {
        const index = this.formGroup().controls.receipts.controls.indexOf(receiptFormGroup);
        if (index === -1) throw new Error('Receipt index not found!');
        this.formGroup().controls.receipts.removeAt(index);
    }

    onMultipleFileUpload(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = Array.from({ length: input.files.length }, (_, index) => index)
            .map((index) => input.files.item(index))
            .filter((file) => this.acceptableFileTypes.includes(getExtension(file.type) as FileType));

        files.forEach((file) => {
            const formGrp = new ReceiptFormGroup(null);
            formGrp.controls.file.setValue(file, { emitEvent: true });
            formGrp.controls.file.markAsDirty();
            this.addReceipt(formGrp);
        });
    }
    // subject that keeps the last 'expanded' state.
    // used to sync 'expanded' state between two expandable panels - Expense Reporting View and Expense Reporting Edit
    // nexted whenever any of those panels is opened/closed
    expenseReportingPanelExpandedSubject = new BehaviorSubject<boolean>(false);

    mdsTabGroup = viewChild<MdsTabGroupComponent>('travelTabGroup');
    mdsTabGroup$ = toObservable(this.mdsTabGroup).pipe(filter(Boolean));
    flightMatTab$ = rxjsComputed(() => this.mdsTabGroup$.pipe(map((tabGroup) => tabGroup.tabs().at(0))));
    trainMatTab$ = rxjsComputed(() => this.mdsTabGroup$.pipe(map((tabGroup) => tabGroup.tabs().at(1))));
    lodgingMatTab$ = rxjsComputed(() => this.mdsTabGroup$.pipe(map((tabGroup) => tabGroup.tabs().at(2))));
    rentalCarMatTab$ = rxjsComputed(() => this.mdsTabGroup$.pipe(map((tabGroup) => tabGroup.tabs().at(3))));
    carServiceMatTab$ = rxjsComputed(() => this.mdsTabGroup$.pipe(map((tabGroup) => tabGroup.tabs().at(4))));
    otherMatTab$ = rxjsComputed(() => this.mdsTabGroup$.pipe(map((tabGroup) => tabGroup.tabs().at(5))));
    createHighlightTabEffect$(opts: { needsTravel$: Observable<boolean>; tab$: Observable<MdsTabComponent> }) {
        return rxjsEffect(() =>
            combineLatest({
                tab: opts.tab$,
                needsTravel: opts.needsTravel$,
            }).pipe(
                tap(({ needsTravel, tab }) => {
                    if (needsTravel) {
                        tab.matTab().labelClass = 'highlight-tonal';
                    } else {
                        tab.matTab().labelClass = undefined;
                    }
                })
            )
        );
    }
    highlightFlightTab$ = this.createHighlightTabEffect$({
        needsTravel$: this.needsFlight$.source$,
        tab$: this.flightMatTab$.source$,
    });
    highlightTrainTab$ = this.createHighlightTabEffect$({
        needsTravel$: this.needsTrain$.source$,
        tab$: this.trainMatTab$.source$,
    });
    highlightLodgingTab$ = this.createHighlightTabEffect$({
        needsTravel$: this.needsLodging$.source$,
        tab$: this.lodgingMatTab$.source$,
    });
    highlightRentalCarTab$ = this.createHighlightTabEffect$({
        needsTravel$: this.needsRentalCar$.source$,
        tab$: this.rentalCarMatTab$.source$,
    });
    highlightCarServiceTab$ = this.createHighlightTabEffect$({
        needsTravel$: this.needsCarService$.source$,
        tab$: this.carServiceMatTab$.source$,
    });
    highlightOtherTab$ = this.createHighlightTabEffect$({
        needsTravel$: this.needsOther$.source$,
        tab$: this.otherMatTab$.source$,
    });
}
